import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import type { DiagnosticTabProps } from "../../DiagnosticSection";

/* -------------------------------------------------------------------------- */
/* os chips de identificação do ambiente                                        */
/* -------------------------------------------------------------------------- */

/** Nome de produto do modelo de ERP. Token desconhecido volta como veio. */
const ERP_LABEL: Record<string, string> = {
  ecc: "SAP ECC",
  s4: "SAP S/4HANA",
  business_one: "SAP Business One",
};

/**
 * A fileira de chips que abre o Ato 1 (#939).
 *
 * O desenho tem cinco. Três saem de medição — release, NetWeaver com banco e
 * paisagem de transporte —, e vêm do `/api/rfc/system-profile`: `CVERS` para o
 * nível de `SAP_APPL` e `SAP_BASIS`, `RFC_SYSTEM_INFO` para o produto de banco,
 * `TMSCSYS` para os sistemas do domínio.
 *
 * **Chip é afirmação, não célula.** Campo sem dado não vira travessão: o chip
 * não existe. Travessão dentro de chip lê como "o sistema respondeu isto".
 *
 * **Versão e tamanho de banco ficam de fora.** O desenho escreve "Oracle 19c ·
 * 1,2 TB" porque o cliente do mock os tinha; nenhuma fonte que a gente leia dá
 * os dois, e escrevê-los seria inventar a parte mais verificável do chip.
 *
 * **O destino não vira chip.** O desenho tem um, e a #872 já tinha decidido
 * deixá-lo de fora do cabeçalho pelo mesmo motivo: a conexão de alvo é opcional
 * no pré-venda e pode não existir. Acrescentei o chip em 25/09 esquecendo dessa
 * decisão, e ele saiu no mesmo dia — a #932 é justamente o caso em que afirmar
 * "destino GROW" está errado, o cliente que só quer ler o próprio ambiente.
 */
export function EnvironmentChips({ snapshots }: Pick<DiagnosticTabProps, "snapshots">) {
  const { t } = useTranslation();
  const system = snapshots?.functional?.system_info;
  if (!system) return null;

  const chips: string[] = [];

  const erp = system.erp_system_model
    ? (ERP_LABEL[system.erp_system_model] ?? system.erp_system_model)
    : undefined;
  // #1789: no S/4 a versão vem do `S4CORE` (`s4_release`); no ECC, do enhancement package.
  const version = system.enhancement_package ?? system.s4_release;
  if (erp) {
    chips.push(version ? `${erp} · ${version}` : erp);
  }

  const stack = [
    system.netweaver_release ? `NetWeaver ${system.netweaver_release}` : undefined,
    system.database_system ?? undefined,
  ].filter(Boolean);
  if (stack.length > 0) chips.push(stack.join(" · "));

  const landscape = (system.landscape ?? []).map((row) => row.system).filter(Boolean);
  if (landscape.length > 0) {
    chips.push(`${t("Landscape")}: ${landscape.join(" / ")}`);
  }

  return (
    <div data-testid="diagnostic-environment-chips" className="flex flex-wrap gap-2">
      {chips.map((chip, i) => (
        <span
          key={chip}
          className={cn(
            "rounded-full px-3 py-1 font-mono text-[11px]",
            i === 0
              ? "bg-[#08254c] text-white"
              : "border border-[#e8ebf0] bg-white text-[#596b85]",
          )}
        >
          {chip}
        </span>
      ))}
    </div>
  );
}
